/**
 * 74.2 step 0 (DECISIONS 353; ARCHITECTURE §5.40): the sky's clock — pure, deterministic, presentation's to read; nothing
 * in the Match.
 *
 * The owner: "some planets could have short days and some planets long days … I don't want to say 'a match is x cycles'
 * just 'a match takes place at a time and depending on the planet time goes on'". So: (i) every body has its own **spin
 * period** from its kind and seed — a rocky world a day of 10–40 hours, a giant a fast 9–17, a moon tidally locked to its
 * parent (its day as long as its orbit); (ii) a match **starts at an hour** of that day, seeded unless the setup pins one
 * (`skyHour`: the Story's dusk); (iii) the sky's time runs from it at **one rate for every world** (`skyRate`: match seconds
 * × the rate = sky seconds), so a short-day world turns visibly faster than a long-day one — the rate is the control, real
 * time among its settings, `off` the static sky 74.1 drew. The orbits run **on rails at true periods** on the same clock
 * (Kepler, closed-form: a giant's inner moons move in a match, the planets do not). No state: everything is a function of
 * the match's seconds and the seed, the same at any tick.
 */
import { SeededRandom } from './random';
import { dpow } from './dmath';
import type { StarSystem, SystemBody } from './systemGen';
import { STAR_PHYSICS, systemForWorld, sizeCategoryOf } from './systemGen';
import type { GameConfig, PlanetArchetype, PlanetState } from '../types';

/** The sky's rate: sky seconds per match second; `off` is the static sky. */
export const SKY_RATES = { off: 0, real: 1, x6: 6, x12: 12, x24: 24, x72: 72 } as const;
export type SkyRateKey = keyof typeof SKY_RATES;
/** The default the shots propose (353: lane I proposes, the owner judges): a 30-minute Story sees six hours of sky — on a 24-hour world a quarter of its day, on a 10-hour world most of one. */
export const DEFAULT_SKY_RATE: SkyRateKey = 'x12';
/** The hour a match starts at, as a fraction of the day: 0 midnight, 0.5 noon; `seeded` draws one. */
export const SKY_HOURS = { seeded: -1, dawn: 0.26, morning: 0.38, noon: 0.5, afternoon: 0.62, dusk: 0.74, night: 0.95 } as const;   // (sunrise and sunset at 0.25 and 0.75 on a world with no tilt: dawn and dusk just inside them, so a match pinned there opens on the sun at the horizon)
export type SkyHourKey = keyof typeof SKY_HOURS;

const HOUR = 3600, DAY = 86400, YEAR = 365.25 * DAY;
/** GM (km³/s²) of a parent by its kind, scaled from a reference body by the cube of its radius (the same density). */
const GM_REF = { gasGiant: { gm: 1.26687e8, radiusKm: 71492 }, iceGiant: { gm: 6.836e6, radiusKm: 24622 }, rocky: { gm: 3.986e5, radiusKm: 6371 }, icy: { gm: 3.986e5, radiusKm: 6371 } } as const;
/** A star's mass in suns from its luminosity (the main sequence's L ∝ M^3.5; a red giant about one and a half suns). */
export function starMassOf(type: StarSystem['star']['type']): number {
  if (type === 'redGiant') return 1.5;
  return dpow(STAR_PHYSICS[type].luminosity, 1 / 3.5);
}

/** A body's orbital period (s): a planet's year from its orbit and its star's mass; a moon's month from its orbit and its parent's size. */
export function orbitPeriodS(system: StarSystem, body: SystemBody): number {
  if (body.parent === null) return YEAR * Math.sqrt(body.orbitAu * body.orbitAu * body.orbitAu / starMassOf(system.star.type));
  const parent = system.bodies[body.parent];
  const ref = GM_REF[parent.kind];
  const gm = ref.gm * dpow(parent.radiusKm / ref.radiusKm, 3);
  const a = body.orbitKm;
  return 2 * Math.PI * Math.sqrt(a * a * a / gm);
}

/** A body's spin period (s), its day: a moon tidally locked (its orbit's period); a giant 9–17 h; a rocky or icy planet 10–40 h, seeded. */
export function spinPeriodS(system: StarSystem, body: SystemBody): number {
  if (body.parent !== null) return orbitPeriodS(system, body);
  const rng = new SeededRandom(`${system.seed}-spin-${body.id}`);
  if (body.kind === 'gasGiant' || body.kind === 'iceGiant') return (9 + rng.next() * 8) * HOUR;
  return 10 * dpow(4, rng.next()) * HOUR;   // log-uniform over 10–40 h
}

/** The hour (a fraction of the day) a match starts at: the pin, or one seeded from the match. */
export function startHourOf(seed: string, pin: SkyHourKey = 'seeded'): number {
  if (pin !== 'seeded') return SKY_HOURS[pin];
  return new SeededRandom(`${seed}-sky-hour`).next();
}

/** The sun's hour angle (rad) at `skySeconds` from a start at `startHour`: 0 at noon, π at midnight, growing with the day. */
export function hourAngle(skySeconds: number, startHour: number, spinPeriodS: number): number {
  const h = startHour + skySeconds / spinPeriodS;
  return (h - 0.5) * 2 * Math.PI;
}

/** The system with every body's orbital angle advanced by `skySeconds` on its rails (a shallow copy; the periods closed-form). */
export function systemAt(system: StarSystem, skySeconds: number): StarSystem {
  if (skySeconds === 0) return system;
  return { ...system, bodies: system.bodies.map(b => ({ ...b, angle: b.angle + 2 * Math.PI * skySeconds / orbitPeriodS(system, b) })) };
}

/** For the Lab and the record: a body's day and year in hours and days. */
export function clockOf(system: StarSystem, body: SystemBody): { dayH: number; yearD: number; locked: boolean } {
  return { dayH: spinPeriodS(system, body) / HOUR, yearD: orbitPeriodS(system, body) / DAY, locked: body.parent !== null };
}

/**
 * 74.11 (a): the match's hour of the day at `seconds` (a fraction: 0.25 sunrise, 0.5 noon, 0.75 sunset), as the sky's clock
 * tells it — the start hour (pinned, else seeded) and the rate over the battle world's day (its system found as the sky
 * finds it, kept per seed and world). A pure function of the config and the seconds: the weather's fronts read it for
 * their hour (core/climate.ts hourWeights); the drawn sun shows the same hour where the match began.
 */
export function matchHour(config: Pick<GameConfig, 'seed' | 'skyHour' | 'skyRate' | 'skyWeights'>, planet: Pick<PlanetState, 'id' | 'archetype' | 'radius'>, seconds: number): number {
  const seed = String(config.seed ?? '');
  const key = `${seed}|${planet.id}|${planet.archetype}|${sizeCategoryOf(planet.radius)}|${JSON.stringify(config.skyWeights ?? null)}`;   // (fix/74.11-memo-keys: the weights are an input of the search)
  let f = hourSystems.get(key);
  if (!f) { const found = systemForWorld(seed, planet.archetype as PlanetArchetype, sizeCategoryOf(planet.radius), { archetypeWeights: config.skyWeights }); f = { system: found.system, body: found.body }; hourSystems.set(key, f); }
  const rate = config.skyRate ?? SKY_RATES[DEFAULT_SKY_RATE], start = config.skyHour ?? startHourOf(seed);
  const h = start + (seconds * rate) / spinPeriodS(f.system, f.body);
  return h - Math.floor(h);
}
const hourSystems = new Map<string, { system: StarSystem; body: SystemBody }>();
